// =====================================================================================
// VideoErrorBoundary - React Error Boundary for Video Components
// =====================================================================================
// Phase 4: Error Handling - Prevents video errors from crashing the entire game

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { videoErrorHandler, ErrorClassification } from '../../services/video/VideoErrorHandler.js';
import type { VideoError } from '../../services/video/types.js';
import { VIDEO_CONFIG, getStackPositionClass } from '../../config/videoConfig.js';
import { useOpenDrawers } from '../../hooks/useOpenDrawers.js';

interface Props {
  children: ReactNode;
  fallbackComponent?: React.ComponentType<VideoErrorBoundaryFallbackProps>;
}

interface State {
  hasError: boolean;
  /**
   * Set once the user dismisses the fallback. The children are known to throw,
   * so re-rendering them would throw again the moment the fallback goes away —
   * a dismiss/retry loop. Dismissing renders nothing at all until an explicit
   * retry.
   */
  dismissed: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorClassification: ErrorClassification | null;
  errorId: string | null;
}

export interface VideoErrorBoundaryFallbackProps {
  error: Error;
  /** Null on the first fallback render: React runs `componentDidCatch` after it. */
  errorInfo: ErrorInfo | null;
  errorClassification: ErrorClassification;
  errorId: string;
  retry: () => void;
  dismiss: () => void;
}

/**
 * Error boundary specifically designed for video components
 * Provides graceful fallback UI and recovery options
 */
export class VideoErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      hasError: false,
      dismissed: false,
      error: null,
      errorInfo: null,
      errorClassification: null,
      errorId: null
    };
  }

  /**
   * Classify here, in the render phase, and not only in `componentDidCatch`.
   *
   * React re-renders the boundary with the derived state *before* the commit
   * phase runs `componentDidCatch`, so a fallback gated on a classification
   * set there would render the throwing children once more on the way to the
   * fallback. `createErrorSummary` is a pure lookup over a static table.
   */
  static getDerivedStateFromError(error: Error): Partial<State> {
    console.error('🎥 [ERROR BOUNDARY] Video error caught:', error);

    const errorSummary = videoErrorHandler.createErrorSummary({
      code: error.name || 'COMPONENT_ERROR',
      message: error.message,
      timestamp: Date.now()
    });

    return {
      hasError: true,
      error,
      errorClassification: errorSummary.classification,
      errorId: errorSummary.errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🎥 [ERROR BOUNDARY] Video component error details:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Create video error object for classification
    const videoError: VideoError = {
      code: error.name || 'COMPONENT_ERROR',
      message: error.message,
      timestamp: Date.now()
    };

    // Get error classification
    const errorSummary = videoErrorHandler.createErrorSummary(videoError);

    this.setState({
      errorInfo,
      errorClassification: errorSummary.classification,
      errorId: errorSummary.errorId
    });

    // Log structured error for debugging
    this.logStructuredError(error, errorInfo, errorSummary);

    // If it's a temporary error, attempt automatic recovery after a delay
    if (errorSummary.classification.isTemporary && errorSummary.classification.shouldRetry) {
      this.scheduleAutoRetry();
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  private logStructuredError(error: Error, errorInfo: ErrorInfo, errorSummary: any) {
    const logData = {
      timestamp: new Date().toISOString(),
      errorId: errorSummary.errorId,
      category: errorSummary.classification.category,
      severity: errorSummary.classification.severity,
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    console.error('🎥 [ERROR BOUNDARY] Structured error log:', logData);

    // In production, you might want to send this to an error reporting service
    // errorReportingService.report(logData);
  }

  private scheduleAutoRetry = () => {
    console.info('🎥 [ERROR BOUNDARY] Scheduling automatic retry in 3 seconds...');

    this.retryTimeoutId = setTimeout(() => {
      // A dismissal outranks a scheduled recovery: the user asked for the
      // video chrome to go away, so it stays away until they retry.
      if (this.state.dismissed) return;
      console.info('🎥 [ERROR BOUNDARY] Attempting automatic recovery...');
      this.handleRetry();
    }, 3000);
  };

  private handleRetry = () => {
    console.info('🎥 [ERROR BOUNDARY] Retrying video component...');

    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    this.setState({
      hasError: false,
      dismissed: false,
      error: null,
      errorInfo: null,
      errorClassification: null,
      errorId: null
    });
  };

  /**
   * Dismiss the fallback without remounting the children.
   *
   * Clearing `hasError` alone put the throwing subtree straight back on
   * screen, where it threw again and brought the fallback back: dismiss did
   * nothing but flicker. Video is optional chrome, so dismissing renders
   * nothing until the user asks to retry.
   */
  private handleDismiss = () => {
    console.info('🎥 [ERROR BOUNDARY] Dismissing error boundary...');

    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    this.setState({ dismissed: true });
  };

  render() {
    if (this.state.dismissed) {
      return null;
    }

    if (this.state.hasError && this.state.error && this.state.errorClassification && this.state.errorId) {
      // Use custom fallback component if provided
      const FallbackComponent = this.props.fallbackComponent;
      if (FallbackComponent) {
        return (
          <FallbackComponent
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            errorClassification={this.state.errorClassification}
            errorId={this.state.errorId}
            retry={this.handleRetry}
            dismiss={this.handleDismiss}
          />
        );
      }

      // Default fallback UI
      return (
        <DefaultVideoErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorClassification={this.state.errorClassification}
          errorId={this.state.errorId}
          retry={this.handleRetry}
          dismiss={this.handleDismiss}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Default fallback component for video errors
 */
function DefaultVideoErrorFallback({
  error,
  errorClassification,
  errorId,
  retry,
  dismiss
}: VideoErrorBoundaryFallbackProps) {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/20 border-red-500/30 text-red-300';
      case 'high': return 'bg-orange-500/20 border-orange-500/30 text-orange-300';
      case 'medium': return 'bg-yellow-500/20 border-yellow-500/30 text-yellow-300';
      case 'low': return 'bg-blue-500/20 border-blue-500/30 text-blue-300';
      default: return 'bg-gray-500/20 border-gray-500/30 text-gray-300';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return '🚨';
      case 'high': return '⚠️';
      case 'medium': return '⚡';
      case 'low': return 'ℹ️';
      default: return '❓';
    }
  };

  const openDrawers = useOpenDrawers();

  return (
    // The video strip at the top lets clicks through; the panel takes them.
    <div
      className={`${getStackPositionClass(openDrawers)} ${VIDEO_CONFIG.styles.zIndex} pointer-events-none`}
      data-lk-theme={VIDEO_CONFIG.theme}
      data-testid="video-error-boundary-fallback"
    >
      <div
        role="alert"
        className={`
          pointer-events-auto w-96 animate-in slide-in-from-top-2
          border rounded-lg shadow-2xl p-4
          ${getSeverityColor(errorClassification.severity)}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">
              {getSeverityIcon(errorClassification.severity)}
            </span>
            <h3 className="font-semibold">Video Service Error</h3>
          </div>
          <button
            onClick={dismiss}
            className="text-gray-400 hover:text-white transition-colors p-1"
            title="Dismiss"
          >
            ✕
          </button>
        </div>

        {/* User Message */}
        <div className="mb-4">
          <p className="text-sm leading-relaxed">
            {errorClassification.userMessage}
          </p>
        </div>

        {/* Recovery Actions */}
        {errorClassification.recoveryActions.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-medium mb-2 opacity-75">What you can do:</h4>
            <div className="space-y-2">
              {errorClassification.recoveryActions.slice(0, 2).map((action, index) => (
                <button
                  key={index}
                  onClick={action.type === 'retry' ? retry : undefined}
                  disabled={action.type !== 'retry'}
                  className={`
                    w-full text-left text-xs p-2 rounded border transition-colors
                    ${action.type === 'retry'
                      ? 'bg-white/10 hover:bg-white/20 border-white/20 cursor-pointer'
                      : 'bg-white/5 border-white/10 cursor-default opacity-75'
                    }
                  `}
                  title={action.description}
                >
                  <span className="font-medium">{action.label}</span>
                  <br />
                  <span className="opacity-75">{action.description}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Technical Details (expandable) */}
        <details className="mb-3">
          <summary className="text-xs cursor-pointer opacity-50 hover:opacity-75">
            Technical Details
          </summary>
          <div className="mt-2 p-2 bg-black/20 rounded text-xs font-mono">
            <div><strong>Error:</strong> {error.name}</div>
            <div><strong>Message:</strong> {error.message}</div>
            <div><strong>Category:</strong> {errorClassification.category}</div>
            <div><strong>Error ID:</strong> {errorId}</div>
          </div>
        </details>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={retry}
            className="
              flex-1 bg-white/10 hover:bg-white/20
              border border-white/20 rounded px-3 py-2
              text-xs font-medium transition-colors
            "
          >
            🔄 Try Again
          </button>
          <button
            onClick={dismiss}
            className="
              px-3 py-2 text-xs font-medium
              text-gray-400 hover:text-white
              transition-colors
            "
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}